import AccordionItem from 'components/common/AccordionItem';
import { useSetDropdownAndRole } from 'hooks/useSetDropdownAndRole';
import { getProjectDetailsFromObservationData } from 'pages/utils';
import { useCallback, useEffect, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { setProjectDetails } from 'redux/conductInspectionSlice';
import { Loader } from 'components/common/Loader';
import {
  IIncidentReport,
  IncidentTask,
  useGetIncidentByIdMutation,
  useGetReportMutation,
  useGetTasksByTypeMutation,
} from 'redux/services/incident';
import { AddIncident } from 'pages/RecordIncident/components/AddIncident';
import { ProjectDetails } from 'pages/ConductInspection/components/ProjectDetails';
import {
  AccessTime,
  ArrowBack,
  Chat,
  FileDownload,
  PendingActions,
  TaskAlt,
} from '@mui/icons-material';
import { Button, Dialog } from '@mui/material';
import { WorkFlowDate } from 'components/common/WorkFlowDate';
import { WorkFlowStatus } from 'components/common/WorkFlowCard/types';
import { ChatConversation } from './ChatConversation';
import { CommentItem } from 'components/ChatComponents/CommentItem';
import './ReviewIncident.scss';
import toast from 'react-hot-toast';
import { downloadIncidentReport } from './downloadIncidentReport';

const ReviewIncident = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { projectId, incidentId } = useParams();
  const [selectedTask, setSelectedTask] = useState<IncidentTask | null>(null);
  const [selectedImage, setSelectedImage] = useState('');

  const [getIncident, { data: incident, isLoading }] =
    useGetIncidentByIdMutation();
  const [getTasks, { data: tasks }] = useGetTasksByTypeMutation();
  const [getReport] = useGetReportMutation();

  const { project } = useSetDropdownAndRole();

  useEffect(() => {
    return () => {
      dispatch(setProjectDetails(null));
    };
  }, [dispatch]);

  const fetchIncident = useCallback(async () => {
    await getIncident({
      projectId,
      id: incidentId,
    });
  }, [getIncident, incidentId, projectId]);

  useEffect(() => {
    if (incidentId && !incident) {
      getTasks({ projectId, id: incidentId });
      fetchIncident();
    }
  }, [fetchIncident, incidentId, incident, getTasks, projectId]);

  useEffect(() => {
    if (project && incident) {
      dispatch(
        setProjectDetails(
          getProjectDetailsFromObservationData(incident, project),
        ),
      );
    }
  }, [project, incident, dispatch]);

  useEffect(() => {
    const taskId = searchParams.get('taskId');

    if (tasks && (selectedTask || taskId)) {
      const task =
        tasks.find(
          (t: IncidentTask) => t._id === (taskId || selectedTask?._id),
        ) ?? null;

      setSelectedTask(task);
    }
  }, [searchParams, selectedTask, tasks]);

  const handleBack = () => {
    const prevPath = searchParams.get('path');

    handleDrawerClose();

    if (prevPath) {
      navigate(prevPath, { replace: true });
    } else {
      navigate(`/project/${projectId}/incident`, { replace: true });
    }
  };

  const getViewTaskDetailsHandler = (task: IncidentTask) => () => {
    setSelectedTask(task);
    navigate(
      `/project/${projectId}/incident/${incident._id}/review?taskId=${task._id}`,
    );
  };

  const handleDrawerClose = () => {
    const prevPath = searchParams.get('path');

    setSelectedTask(null);
    navigate(
      `/project/${projectId}/incident/${incident._id}/review?path=${
        prevPath || ''
      }`,
      { replace: true },
    );
  };

  const handleChatUpdate = () => {
    getTasks({
      projectId,
      id: incidentId,
    });
  };

  const handleCloseImageDialog = () => setSelectedImage('');
  const getImageClickHandler = (img: string) => () => setSelectedImage(img);

  const renderSubtitle = () => {
    return (
      <div className="subtitle d-flex w-100 align-items-center">
        <div className="d-flex flex-column w-100 mx-2 my-1">
          <span className="h5">{incident?.incidentType}</span>
          <span className="h6">{incident?.description ?? ''}</span>
        </div>
        {incident?.images?.map(({ url }: { url: string }, i: number) => (
          <div key={i} className="d-flex flex-wrap image-container">
            <img src={url} alt="" onClick={getImageClickHandler(url)} />
          </div>
        ))}
      </div>
    );
  };

  const handleDownload = async () => {
    toast.success('Report will be downloaded shortly');
    const res: any = await getReport({
      id: incidentId,
    });

    if (res.data as IIncidentReport) {
      downloadIncidentReport(res.data, project?.team ?? []);
    } else {
      toast.error('Report generation failed. Please try again later');
    }
  };

  return project && !isLoading && incident ? (
    <div className="review-incident d-flex flex-column">
      <div className="review-sub-header p-2">
        <span className="h5">{`# ${incident.displayId || ''}`}</span>
        <span className="h5 mx-2">{t('review_incident_sub_header')}</span>
      </div>
      <div className="d-flex flex-column w-100 h-100 p-2 flex-grow-1 overflow-auto">
        <div className="d-flex flex-column form-content">
          <Accordion
            defaultActiveKey={
              searchParams.get('taskId') ? 'incident-tag' : '-1'
            }
          >
            <AccordionItem
              eventKey="project_details"
              text={t('label_project_details')}
              children={
                <ProjectDetails
                  observation
                  disabled
                  noFooter
                  project={project}
                />
              }
            />
            <AccordionItem
              eventKey="incident_details"
              text={t('title_incident')}
              children={
                <AddIncident incident={incident} disabled onSubmit={() => {}} />
              }
            />
            <AccordionItem
              eventKey="incident-tag"
              text={t('label_incident_task')}
              leftAction={
                tasks?.length > 0 &&
                tasks?.[0]?.status !== WorkFlowStatus.CLOSE ? (
                  <div className="d-flex align-items-center">
                    <PendingActions className="mx-2" color="warning" />
                    <WorkFlowDate
                      date={incident?.rcaDate}
                      status={incident?.status}
                      displayTag
                      label={<AccessTime color="inherit" />}
                    />
                  </div>
                ) : (
                  tasks?.length > 0 && (
                    <TaskAlt className="mx-2" color="success" />
                  )
                )
              }
              children={
                tasks?.length > 0 ? (
                  <div className="d-flex flex-column">
                    {renderSubtitle()}
                    <ul className="list-group list-style-none">
                      {tasks?.map((task: IncidentTask) => (
                        <li
                          key={task._id}
                          className="list-group-item d-flex flex-column w-100"
                        >
                          <div className="d-flex my-2 flex-nowrap">
                            {task.status === WorkFlowStatus.CLOSE ? (
                              <TaskAlt className="mx-2" color="success" />
                            ) : (
                              <PendingActions
                                className="mx-2"
                                color="warning"
                              />
                            )}
                            <div className="task-title overflow-hidden">
                              <span>{task.name}</span>
                            </div>
                          </div>
                          <div className="d-flex justify-content-end align-items-center w-100 flex-wrap">
                            <div className="task-info d-flex flex-column w-100">
                              {task.comments.length > 0 && (
                                <div className="d-flex flex-column last-comment-details">
                                  <CommentItem
                                    comment={
                                      task.comments[task.comments.length - 1]
                                    }
                                    component="div"
                                    project={project}
                                  />
                                </div>
                              )}
                              <div className="d-flex justify-content-end">
                                <Button
                                  className="ml-2 mt-2 mx-1"
                                  variant="contained"
                                  startIcon={<Chat sx={{ color: '#fff' }} />}
                                  onClick={getViewTaskDetailsHandler(task)}
                                >
                                  {t('btn_view')}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <span className="h5">No snag task raised</span>
                )
              }
            />
          </Accordion>
        </div>
        {selectedTask && (
          <ChatConversation
            selectedTask={selectedTask}
            project={project}
            description={renderSubtitle()}
            onDrawerClose={handleDrawerClose}
            onUpdate={handleChatUpdate}
          />
        )}
        <div className="d-flex w-100 justify-content-between">
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleBack}
          >
            {t('btn_back')}
          </Button>
          <Button
            variant="contained"
            startIcon={<FileDownload sx={{ color: '#fff' }} />}
            onClick={handleDownload}
          >
            {t('btn_download')}
          </Button>
        </div>
      </div>
      <Dialog
        className="captured-image-dialog"
        open={selectedImage.length > 0}
        onClose={handleCloseImageDialog}
      >
        <img className="captured-image" alt="captured" src={selectedImage} />
      </Dialog>
    </div>
  ) : (
    <Loader />
  );
};

export default ReviewIncident;
